import React from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartData as ChartJSData,
  ChartOptions
} from 'chart.js'
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2'
import { ChartData } from '@/store/slices/opsChatSlice'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
)

interface ChartComponentProps {
  chartData: ChartData
  className?: string
}

const ChartComponent: React.FC<ChartComponentProps> = ({ chartData, className = '' }) => {
  // Default Chart.js options with responsive design
  const defaultOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        display: true
      },
      title: {
        display: true,
        text: chartData.title,
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1
      }
    },
    ...chartData.options
  }

  // Generate default colors for datasets if not provided
  const generateColors = (dataLength: number, isBackground: boolean = true) => {
    const baseColors = [
      '#3B82F6', // Blue
      '#10B981', // Green  
      '#F59E0B', // Amber
      '#EF4444', // Red
      '#8B5CF6', // Purple
      '#06B6D4', // Cyan
      '#F97316', // Orange
      '#84CC16', // Lime
      '#EC4899', // Pink
      '#6B7280'  // Gray
    ]
    
    return Array.from({ length: dataLength }, (_, i) => {
      const baseColor = baseColors[i % baseColors.length]
      if (isBackground) {
        // Add transparency for background colors
        return baseColor + '80' // 50% opacity
      }
      return baseColor
    })
  }

  // Prepare Chart.js data with default colors
  const preparedData: ChartJSData = {
    labels: chartData.data.labels,
    datasets: chartData.data.datasets.map((dataset, index) => ({
      ...dataset,
      backgroundColor: dataset.backgroundColor || 
        (chartData.chartType === 'pie' || chartData.chartType === 'doughnut' 
          ? generateColors(chartData.data.labels.length)
          : generateColors(1)[0]),
      borderColor: dataset.borderColor || 
        (chartData.chartType === 'pie' || chartData.chartType === 'doughnut' 
          ? generateColors(chartData.data.labels.length, false)
          : generateColors(1, false)[0]),
      borderWidth: dataset.borderWidth || 2
    }))
  }

  // Render different chart types
  const renderChart = () => {
    switch (chartData.chartType) {
      case 'bar':
        return <Bar data={preparedData as any} options={defaultOptions as any} />
      case 'line':
        return <Line data={preparedData as any} options={defaultOptions as any} />
      case 'pie':
        return <Pie data={preparedData as any} options={defaultOptions as any} />
      case 'doughnut':
        return <Doughnut data={preparedData as any} options={defaultOptions as any} />
      case 'table':
        return renderTable()
      default:
        return <div className="text-center text-muted-foreground">Unsupported chart type: {chartData.chartType}</div>
    }
  }

  // Format a numeric cell value based on column label
  const formatCellValue = (value: number | undefined, columnLabel: string): string => {
    if (value === undefined || value === null) return 'N/A'
    const label = columnLabel.toLowerCase()
    // Percentage columns: PowerBI returns decimals (0.68 = 68%)
    // Detect by "+/- %", "sell-thru", or "discount" in the column name
    if (label.includes('+/- %') || label.includes('sell-thru') || label.includes('discount')) {
      return `${(value * 100).toFixed(1)}%`
    }
    // Default: number with comma separators, up to 2 decimal places
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
  }

  // Get column groups from backend options (set for pivoted tables)
  // Format: { groups: ["0.99", "1.99", ...], metrics: ["IPO", "IPO +/- LY"] }
  const getColumnGroups = (): { groups: Array<{ group: string; metrics: string[] }>; metricNames: string[] } | null => {
    const cg = chartData.options?.columnGroups
    if (!cg || !cg.groups || !cg.metrics || cg.metrics.length < 1) return null
    // Only use grouped headers when there are multiple metrics per group
    if (cg.metrics.length < 2) return null
    return {
      groups: cg.groups.map((g: string) => ({ group: g, metrics: cg.metrics })),
      metricNames: cg.metrics
    }
  }

  // Render table view for table chart type
  const renderTable = () => {
    if (!chartData.data.labels || !chartData.data.datasets[0]) {
      return <div className="text-center text-muted-foreground">No data available</div>
    }

    const firstColHeader = chartData.options?.firstColumnHeader || 'Category'
    const columnGroups = getColumnGroups()

    return (
      <div className="overflow-x-auto max-w-full">
        <table className="w-full table-auto border border-gray-300 dark:border-gray-600">
          <thead className="bg-gray-100 dark:bg-gray-700">
            {columnGroups ? (
              <>
                {/* Two-level header: group row + metric row */}
                <tr>
                  <th rowSpan={2} className="px-3 py-2 text-left border-b border-r border-gray-300 dark:border-gray-600 font-semibold sticky left-0 bg-gray-100 dark:bg-gray-700 z-10">
                    {firstColHeader}
                  </th>
                  {columnGroups.groups.map((g, i) => (
                    <th key={i} colSpan={columnGroups.metricNames.length} className="px-2 py-1 text-center border-b border-l border-gray-300 dark:border-gray-600 font-semibold whitespace-nowrap">
                      {g.group}
                    </th>
                  ))}
                </tr>
                <tr>
                  {columnGroups.groups.map((g, gi) =>
                    g.metrics.map((m, mi) => (
                      <th key={`${gi}-${mi}`} className="px-2 py-1 text-right border-b border-gray-300 dark:border-gray-600 text-xs font-medium text-muted-foreground whitespace-nowrap">
                        {m}
                      </th>
                    ))
                  )}
                </tr>
              </>
            ) : (
              /* Single-level header */
              <tr>
                <th className="px-3 py-2 text-left border-b border-gray-300 dark:border-gray-600 font-semibold sticky left-0 bg-gray-100 dark:bg-gray-700 z-10">
                  {firstColHeader}
                </th>
                {chartData.data.datasets.map((dataset, index) => (
                  <th key={index} className="px-3 py-2 text-right border-b border-gray-300 dark:border-gray-600 font-semibold whitespace-nowrap">
                    {dataset.label}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {chartData.data.labels.map((label, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="px-3 py-1.5 border-b border-gray-200 dark:border-gray-700 font-medium sticky left-0 bg-card z-10 whitespace-nowrap">
                  {label}
                </td>
                {chartData.data.datasets.map((dataset, colIndex) => (
                  <td key={colIndex} className="px-3 py-1.5 border-b border-gray-200 dark:border-gray-700 text-right tabular-nums text-sm">
                    {formatCellValue(dataset.data[rowIndex], dataset.label)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className={`bg-card rounded-lg border p-6 shadow-sm w-full max-w-full ${className}`}>
      {/* Chart metadata */}
      {chartData.metadata?.generatedAt && (
        <div className="text-xs text-muted-foreground mb-4">
          Generated at {new Date(chartData.metadata.generatedAt).toLocaleString()}
          {chartData.metadata.source && ` • Source: ${chartData.metadata.source}`}
        </div>
      )}
      
      {/* Chart container */}
      <div className={chartData.chartType === 'table' ? 'w-full' : 'h-96 w-full'}>
        {renderChart()}
      </div>
    </div>
  )
}

export default ChartComponent